// "Fix what hasn't been signed yet" (Documents tab PR 10b): the parts of a
// package that a correction must leave exactly as they went out, and who
// is locked. Pure, so the node pool proves it.
//
// CONSUMER: PrepareAndSend.tsx (the Send gate in correction mode) and
// DocumentsTab.tsx (which signer keys are locked).

import { isGhost } from "@shared/esign/ghost-offers";
import { printsByStamp } from "@shared/esign/field-types";

import type { PrepField, PrepStamp } from "./prepare-helpers";

const r = (v: number) => Math.round(v * 2) / 2;

/**
 * The locked part of one document, as one comparable string: the boxes
 * of people who have signed (where and what), the boxes printed into the
 * file (with their text, since that is printed), and the stamped
 * signatures. Two documents with the same string have the same locked
 * part, whatever order the screen holds them in.
 */
export function lockedSignature(
  fields: readonly PrepField[],
  stamps: readonly PrepStamp[],
  lockedKeys: ReadonlySet<string>,
): string {
  const boxes = fields
    .filter((f) => !isGhost(f) && (lockedKeys.has(f.signerKey) || printsByStamp(f)))
    .map((f) =>
      [
        f.signerKey,
        f.fieldType,
        f.pageNumber,
        r(f.x),
        r(f.y),
        r(f.width),
        r(f.height),
        printsByStamp(f) ? (f.value ?? "") : "",
      ].join("|"),
    )
    .sort();
  const marks = stamps.map((s) => [s.pageNumber, r(s.x), r(s.y), r(s.width), r(s.height)].join("|")).sort();
  return JSON.stringify([boxes, marks]);
}

/**
 * The signer keys (as the send screen keys people) of everyone on the
 * package who has signed: a contact by contact id, anyone else by their
 * place among the draft's guests, matched on email.
 */
export function signedSignerKeys(
  signers: readonly { contactId: string | null; email: string; name: string; signed: boolean }[],
  guests: readonly { email: string }[],
): { keys: string[]; names: string[] } {
  const keys: string[] = [];
  const names: string[] = [];
  for (const s of signers) {
    if (!s.signed) continue;
    names.push(s.name);
    if (s.contactId) {
      keys.push(s.contactId);
      continue;
    }
    const i = guests.findIndex((g) => g.email.trim().toLowerCase() === s.email.trim().toLowerCase());
    if (i >= 0) keys.push(`guest:${i}`);
  }
  return { keys, names };
}
